import { dispatch } from '@omni/kit/ActionHandler';
import Downloader, {
  IDownloaderResponse,
  NOT_MODIFIED,
} from '@omni/kit/Downloader';
import { BridgeAction } from '@omni/kit/Types';
import { isUrlAuthorizedForProviders } from '@omni/kit/auth';
import { SUBSPLASH_AUTH_PROVIDER_ID } from '@omni/kit/constants/identifiers';
import { useShellContext } from '@omni/kit/contexts/ShellContext';
import useShellAuthProviders from '@omni/kit/contexts/ShellContext/hooks/useShellAuthProviders';
import CacheService from '@omni/kit/services/CacheService';
import {
  NativeTopBarStyle,
  PresentationStyle,
  dismissReactNativeModal,
  getNativeBottomBarHeight,
  getNativeTopBarHeight,
  isCurrentNavigationControllerModal,
  setTopBarStyle,
  setTopBarTitle,
} from '@omni/kit/utilities/NativeHelpers';
import { getUserLocation } from '@omni/kit/utilities/UserLocation';
import { getLocationPermission } from '@omni/kit/utilities/getLocationPermission';
import { requestLocation } from '@omni/kit/utilities/requestLocation';
import { Location } from '@omni/kit/utilities/requestLocation.types';
import React, {
  MutableRefObject,
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  EmitterSubscription,
  NativeEventEmitter,
  NativeModules,
  Platform,
  ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { RenderBlockFunction } from './blocks/renderBlock.types';
import { BlockData, ErrorBlockData, IInsetStyles } from './blocks/types';
import { getStyleForInsets } from './blocks/utilities';
import { FallbackFileName, getFallbackFile } from './fallbacks';
import { generateErrorPage } from './templates/examples/GeneratePage';
import {
  BlockPageContentData,
  BlockPageErrorData,
  BlockPageProps,
} from './types';
import {
  getActionUrl,
  isBlockPageContentHandler,
  isLocationBasedAction,
} from './utilities';

const debug = require('debug')('tca:blocks:BlockPageContext');

interface ProviderProps {
  children?: React.ReactNode;
  props: BlockPageProps;
  renderBlock: RenderBlockFunction;
}

interface ContextData {
  addressRequired: boolean;
  align: 'start' | 'end' | 'center' | undefined;
  animationEnabled: boolean;
  appKey: string | undefined;
  authProviderId: string | undefined;
  blockPageDeltaInsetTop: number;
  blockPageDeltaInsetBottom: number;
  blockPageUrl: string | undefined;
  blocksData: Array<BlockData | ErrorBlockData>;
  clientBrandColor: string | undefined;
  contentData: BlockPageContentData | null;
  guestToken: string | undefined;
  includeCloseButton: boolean;
  includeLogoutButton: boolean;
  isBlockPageEdited: boolean;
  isBlockPageLoading: boolean;
  isBlockPageRefreshing: boolean;
  isBlockPageReplacing: boolean;
  largeSaveButtonEnabled: boolean;
  renderBlock: RenderBlockFunction;
  dispatchAction: (
    action: BridgeAction | null | undefined,
    callback?: (event: any) => void
  ) => Promise<void>;
  onDismiss: () => void;
  refreshBlockPage: () => void;
  setCurrentTopBarStyle: (topBarStyle: NativeTopBarStyle | undefined) => void;
  setIsBlockPageEdited: (isEdited: boolean) => void;
  setIsBlockPageLoading: (isLoading: boolean) => void;
  setIsBlockPageRefreshing: (isRefreshing: boolean) => void;
  submitActionRef: MutableRefObject<() => void>;
  getInsetStyleForBlock: (index: number) => ViewStyle;
  getBlockExtensions: (index: number) => {
    extendTop?: number;
    extendBottom?: number;
  };
  setContentHeight: (height: number) => void;
  setViewportHeight: (height: number) => void;
}

const BlockPageContext = createContext<ContextData>({
  addressRequired: false,
  align: undefined,
  animationEnabled: false,
  appKey: undefined,
  authProviderId: undefined,
  blockPageDeltaInsetTop: 0,
  blockPageDeltaInsetBottom: 0,
  blockPageUrl: undefined,
  blocksData: [],
  contentData: null,
  clientBrandColor: undefined,
  guestToken: undefined,
  includeCloseButton: true,
  includeLogoutButton: true,
  isBlockPageEdited: false,
  isBlockPageLoading: false,
  isBlockPageRefreshing: false,
  isBlockPageReplacing: false,
  largeSaveButtonEnabled: false,
  renderBlock: () => null,
  dispatchAction: async () => undefined,
  onDismiss: () => undefined,
  refreshBlockPage: () => undefined,
  setCurrentTopBarStyle: () => undefined,
  setIsBlockPageEdited: () => undefined,
  setIsBlockPageLoading: () => undefined,
  setIsBlockPageRefreshing: () => undefined,
  submitActionRef: { current: () => undefined },
  getInsetStyleForBlock: () => ({}),
  getBlockExtensions: () => ({}),
  setContentHeight: () => undefined,
  setViewportHeight: () => undefined,
});

// Helper function to safely get the origin
const getOrigin = (): string => {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return window.location.origin;
  }

  // For native platforms, return an empty string to keep URLs relative
  return '';
};

export const BlockPageContextProvider = ({
  children,
  props,
  renderBlock,
}: ProviderProps): null | ReactElement => {
  const {
    addressRequired = false,
    animationEnabled = true,
    authProviderId,
    clientBrandColor,
    data,
    fallbackResource,
    guestToken,
    largeSaveButtonEnabled = false,
    includeCloseButton = true,
    includeLogoutButton = true,
    presentationStyle,
    suppressSystemInsets,
    topBarStyle,
    url,
    onDismiss = dismissReactNativeModal,
  } = props;

  const { app, tokens, dispatchEvent } = useShellContext();
  const { targetProviders } = useShellAuthProviders();
  const insets = useSafeAreaInsets();

  const appKey = app.appKey;

  const accessToken = useMemo(() => {
    return tokens?.accessTokens?.[authProviderId ?? SUBSPLASH_AUTH_PROVIDER_ID];
  }, [authProviderId, tokens?.accessTokens]);

  const [blockPageUrl, setBlockPageUrl] = useState<string | undefined>(url);
  const [initialBlockPageUrl] = useState<string | undefined>(url); // Store the original URL from props

  const [contentData, setContentData] = useState<BlockPageContentData | null>(
    null
  );
  const [errorCode, setErrorCode] = useState(0);
  const [errorData, setErrorData] = useState<BlockPageErrorData | null>(null);
  const [blocksData, setBlocksData] = useState<
    Array<BlockData | ErrorBlockData>
  >([]);
  const [align, setAlign] = useState<'start' | 'end' | 'center' | undefined>(
    undefined
  );
  const [isBlockPageEdited, setIsBlockPageEdited] = useState(false);
  const [isBlockPageLoading, setIsBlockPageLoading] = useState(false);
  const [isBlockPageRefreshing, setIsBlockPageRefreshing] = useState(false);
  const [isBlockPageReplacing, setIsBlockPageReplacing] = useState(false);

  const [initialTopBarStyle, setInitialTopBarStyle] = useState(topBarStyle);
  const [currentTopBarStyle, setCurrentTopBarStyle] =
    useState(initialTopBarStyle);
  const [blockPageDeltaInsetTop, setBlockPageDeltaInsetTop] = useState(0);
  const [blockPageDeltaInsetBottom, setBlockPageDeltaInsetBottom] = useState(0);
  const [deltaInsetRetrieved, setDeltaInsetRetrieved] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);

  const submitActionRef = useRef<() => void>(
    () => undefined
  ) as MutableRefObject<() => void>;

  const _loadCachedFeed = async (): Promise<IDownloaderResponse | void> => {
    if (!blockPageUrl) return;

    const token = accessToken?.replace('Bearer ', '');

    return await CacheService.getCachedResponse({
      url: blockPageUrl,
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    });
  };

  const _downloadFeed = async (
    cachedResponse?: IDownloaderResponse,
    impression?: boolean
  ): Promise<void> => {
    if (!blockPageUrl) return;

    const context = 'network';

    const extraHeaders: { Authorization?: string } = {};

    if (accessToken && authProviderId) {
      const token = accessToken?.replace('Bearer ', '');
      extraHeaders.Authorization = `Bearer ${token}`;
    }

    let url = blockPageUrl;

    // DISP-6002: supports location-based block handler actions
    // so that the initial request can be made with the correct location
    // if the permission is already granted by the user

    if (isLocationBasedAction({ handler: 'blockPage', url })) {
      try {
        const locationPermission = await getLocationPermission();
        const locationGranted = locationPermission === 'granted';

        let location: Location | undefined;

        if (locationGranted) {
          location = await getUserLocation();
        }

        url = url
          .replace('{lat}', location?.latitude?.toString() ?? '0')
          .replace('{lng}', location?.longitude?.toString() ?? '0');
      } catch {}
    }

    Downloader.downloadUrl({
      url: url,
      options: {
        appKey: appKey,
        authProviderId: authProviderId,
        extraHeaders,
        impression: impression,
        method: 'GET',
        targetProviders: targetProviders,
      },
      cachedResponse,
    })
      .then((response) => {
        _handleFeedResponse(response, context);
        setIsBlockPageRefreshing(false);
        setIsBlockPageReplacing(false);
      })
      .catch((err) => {
        const status = (err?.response?.status as number) || 504;
        _handleFeedLoadError(status, err, context);
        setIsBlockPageRefreshing(false);
        setIsBlockPageReplacing(false);
      });
  };

  const _handleFeedLoadError = (
    code: number,
    err?: {
      response: {
        headers?: Record<string, unknown>;
        request?: { responseURL: string };
      };
    },
    context?: string
  ) => {
    debug(
      'Error loading feed data',
      '| Context:',
      context,
      '| Code:',
      code,
      '| Error:',
      err
    );

    if (
      NativeModules.ReactPlatformBridge != null &&
      (code === 401 || code === 403)
    ) {
      debug('Prompt user to login');

      dispatchEvent({
        type: 'AUTHENTICATE_USER',
        openInNewTab: true,
        targetAuthProviderId: authProviderId || SUBSPLASH_AUTH_PROVIDER_ID,
      });

      /**
       * 'setErrorCode' causes an error screen to render,
       * so here we are opting out of that error code/screen for the case where
       * we are prompting a user to login
       */
    } else {
      setErrorCode(code);
    }
  };

  const _handleErrorCode = (code: number): void => {
    if (code) {
      let fallback: BlockPageContentData | undefined;

      if (fallbackResource) {
        fallback = getFallbackFile(
          fallbackResource as FallbackFileName
        ) as BlockPageContentData;
      }

      if (fallback?.blocks && !contentData) {
        setContentData(fallback as BlockPageContentData);
      } else {
        const data = generateErrorPage(code, (): void => {
          setErrorCode(0); // dismiss error
          _downloadFeed(undefined, false); // retry feed request
        });

        setErrorData(data);
      }
    } else {
      setErrorData(null);
    }
  };

  const _handleFeedResponse = async (
    response?: { body?: BlockPageContentData; status?: number },
    context?: string
  ) => {
    if (!response && context === 'cache') {
      debug('cache miss');

      return;
    }

    debug(
      `Handle response. Context: ${context} Status: ${response?.status}. ` +
        `URL: ${blockPageUrl}`
    );

    if (response?.status === NOT_MODIFIED) return;

    let status = response?.status ?? 550;
    const body = response?.body ?? null;

    if (status < 400 && !body?.blocks) {
      status = 550;
      debug('Response data is malformed.  No blocks found.');
    }

    if (status < 400) {
      setContentData(body);
    } else {
      _handleFeedLoadError(status, undefined, context);
    }
  };

  useEffect((): void => {
    const dataObject = typeof data === 'string' ? JSON.parse(data) : data;

    if (dataObject?.blocks) {
      setContentData(dataObject as BlockPageContentData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fetch feed on mount and whenever auth changes
  useEffect(() => {
    _loadCachedFeed().then((cachedResponse) => {
      const initialRender = !contentData && !errorData;

      if (initialRender) {
        _handleFeedResponse(cachedResponse as IDownloaderResponse, 'cache');
      }

      const trackImpression = initialRender;
      _downloadFeed(cachedResponse as IDownloaderResponse, trackImpression);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    if (
      Platform.OS === 'android' &&
      (topBarStyle === NativeTopBarStyle.Transparent ||
        topBarStyle === NativeTopBarStyle.TransparentOnLight ||
        topBarStyle === NativeTopBarStyle.TransparentOnDark)
    ) {
      getNativeTopBarHeight()
        .then((height) => {
          setBlockPageDeltaInsetTop(height);
          setDeltaInsetRetrieved(true);
        })
        .catch(() => {
          setDeltaInsetRetrieved(true);
        });
    } else if (
      Platform.OS === 'ios' &&
      presentationStyle === PresentationStyle.Default
    ) {
      // On iOS, get the bottom bar height only if navigation controller is not a modal
      isCurrentNavigationControllerModal()
        .then((isModal) => {
          if (!isModal) {
            return getNativeBottomBarHeight();
          }

          return Promise.resolve(0);
        })
        .then((height) => {
          // the iOS bottom bar height includes the safe area inset,
          // so we need to subtract the safe area inset to get the delta
          const deltaInset = height > 0 ? height - insets.bottom : 0;
          setBlockPageDeltaInsetBottom(deltaInset);
          setDeltaInsetRetrieved(true);
        })
        .catch(() => {
          setDeltaInsetRetrieved(true);
        });
    } else {
      setDeltaInsetRetrieved(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * TODO: move this native auth error event listener to AuthContext.
   * Keeping here for now to reduce regression risk in 5.17
   */
  useEffect(() => {
    const listeners = _registerEmitters();

    return () => {
      listeners?.forEach((listener) => {
        listener?.remove();
      });
    };
  }, []);

  const _registerEmitters = (): EmitterSubscription[] | void => {
    if (!NativeModules.ReactPlatformBridge) return;

    const ReactPlatformBridgeEventEmitter = new NativeEventEmitter(
      NativeModules.ReactPlatformBridge
    );

    const authorizationFailedSubscription =
      ReactPlatformBridgeEventEmitter.addListener(
        'AuthorizationError',
        (_error) => setErrorCode(401)
      );

    return [authorizationFailedSubscription];
  };

  useEffect(() => {
    _handleErrorCode(errorCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorCode]);

  useEffect(() => {
    const dataObject = typeof data === 'string' ? JSON.parse(data) : data;

    if (dataObject?.blocks) {
      setContentData(dataObject as BlockPageContentData);
    }
  }, [data]);

  useEffect(() => {
    if (currentTopBarStyle) {
      setTopBarStyle(
        currentTopBarStyle,
        false /* updateInsets */,
        true /* animated */
      );
    }
  }, [currentTopBarStyle]);

  useEffect(() => {
    if (initialTopBarStyle) {
      setTopBarStyle(
        initialTopBarStyle,
        true /* updateInsets */,
        true /* animated */
      );
    }
  }, [initialTopBarStyle]);

  useEffect(() => {
    if (deltaInsetRetrieved && (contentData || errorData)) {
      const title = contentData?.title ?? errorData?.title;

      if (title) setTopBarTitle(title);

      const align = contentData ? contentData.align : errorData?.align;

      setAlign(align);

      const contentBlocks = contentData?.blocks ?? errorData?.blocks;

      if (!contentBlocks) return;

      setBlocksData(contentBlocks);
    }
  }, [deltaInsetRetrieved, contentData, errorData]);

  // Listen for Focus events to update title when tab becomes active
  // Relevant for block page when used in native subtabs (INT-397)
  useEffect(() => {
    const { ReactPlatformBridge } = NativeModules;

    if (!ReactPlatformBridge || !props.componentId) return;

    const ReactPlatformBridgeEventEmitter = new NativeEventEmitter(
      ReactPlatformBridge
    );

    const focusListener = ReactPlatformBridgeEventEmitter.addListener(
      'Focus',
      (event: { componentId: string; focused: boolean }) => {
        // Only act if this event is for our specific component and it's gaining focus
        if (
          event.componentId === props.componentId &&
          event.focused &&
          deltaInsetRetrieved &&
          (contentData || errorData)
        ) {
          const title = contentData?.title ?? errorData?.title;

          if (title) {
            setTopBarTitle(title);
          }
        }
      }
    );

    return () => {
      focusListener?.remove();
    };
  }, [props.componentId, deltaInsetRetrieved, contentData, errorData]);

  useEffect(() => {
    if (isBlockPageRefreshing) {
      _loadCachedFeed().then((cachedResponse) => {
        _downloadFeed(cachedResponse as IDownloaderResponse, false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBlockPageRefreshing]);

  useEffect(() => {
    if (isBlockPageReplacing) {
      _loadCachedFeed().then((cachedResponse) => {
        _handleFeedResponse(cachedResponse as IDownloaderResponse, 'cache');
        _downloadFeed(cachedResponse as IDownloaderResponse, false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBlockPageReplacing]);

  const refreshBlockPage = useCallback(() => {
    setIsBlockPageRefreshing(true);
  }, []);

  const replaceBlockPage = async (url: string): Promise<void> => {
    const authorized = await isUrlAuthorizedForProviders({
      providers: targetProviders || [],
      url: url,
    });

    if (authorized || __DEV__) {
      setBlockPageUrl(url);
      setIsBlockPageReplacing(true);
    }
  };

  const resetToInitialUrl = useCallback(async (): Promise<void> => {
    if (!initialBlockPageUrl) return;

    // If we're not at the initial URL, navigate back to it
    if (blockPageUrl !== initialBlockPageUrl) {
      const authorized = await isUrlAuthorizedForProviders({
        providers: targetProviders || [],
        url: initialBlockPageUrl,
      });

      if (authorized || __DEV__) {
        setBlockPageUrl(initialBlockPageUrl);
        setIsBlockPageReplacing(true);
      }
    } else {
      // Already at the initial URL, just refresh the content
      refreshBlockPage();
    }
  }, [initialBlockPageUrl, blockPageUrl, targetProviders, refreshBlockPage]);

  // Listen for reload events with different behaviors based on needsReloadInitialContent
  useEffect(() => {
    const { ReactPlatformBridge } = NativeModules;

    if (!ReactPlatformBridge || !props.componentId) return;

    const ReactPlatformBridgeEventEmitter = new NativeEventEmitter(
      ReactPlatformBridge
    );

    const reloadListener = ReactPlatformBridgeEventEmitter.addListener(
      'Reload',
      (event: { componentId: string; needsReloadInitialContent: boolean }) => {
        // Only act if this event is for our specific component
        if (event.componentId === props.componentId) {
          if (event.needsReloadInitialContent) {
            // Reset to initial URL (e.g. from main tab tap)
            resetToInitialUrl();
          } else {
            // Refresh current content (e.g. from normal retry/login)
            refreshBlockPage();
          }
        }
      }
    );

    return () => {
      reloadListener?.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshBlockPage, resetToInitialUrl]);

  // Listen for app user settings updates to refresh content
  useEffect(() => {
    const { ReactPlatformBridge } = NativeModules;

    if (!ReactPlatformBridge) return;

    const ReactPlatformBridgeEventEmitter = new NativeEventEmitter(
      ReactPlatformBridge
    );

    const listener = ReactPlatformBridgeEventEmitter.addListener(
      'NotifyAppUserSettingsUpdated',
      () => {
        refreshBlockPage();
      }
    );

    return () => {
      listener?.remove();
    };
  }, [refreshBlockPage]);

  const dispatchAction = async (
    action: BridgeAction | null | undefined,
    callback?: (event: any) => void
  ): Promise<void> => {
    if (action?.handler === 'app' && action?.command === 'reloadActiveScreen') {
      refreshBlockPage();

      return;
    }

    if (
      action?.handler === 'app' &&
      action?.command === 'reloadActiveScreenWithInitialContent'
    ) {
      resetToInitialUrl();

      return;
    }

    // Convert relative URLs to absolute URLs
    if (action) {
      if (action.url?.startsWith('/')) {
        action.url = `${getOrigin()}${action.url}`;
      }

      if (action.contentUrl?.startsWith('/')) {
        action.contentUrl = `${getOrigin()}${action.contentUrl}`;
      }
    }

    const url = getActionUrl(action);

    if (isBlockPageContentHandler(action)) {
      // DISP-4731: enterprise use-case to have buttons that can replace a block page
      replaceBlockPage(url);
    } else if (isLocationBasedAction(action)) {
      try {
        // DISP-6002: when a user presses a location-based block handler action,
        // we need to request the location permission and replace the action url
        // with the location coordinates
        const location = await requestLocation();

        if (location) {
          const urlWithLocation = url
            ?.replace('{lat}', location.latitude.toString())
            .replace('{lng}', location.longitude.toString()) as string;

          dispatch(
            {
              ...action,
              ...(action?.url
                ? { url: urlWithLocation }
                : { contentUrl: urlWithLocation }),
            },
            callback
          );
        }
      } catch {}
    } else {
      dispatch(action, callback);
    }
  };

  const getInsetStyleForBlock = (index: number): ViewStyle => {
    // a nested block page e.g. frame block will not have system insets
    // since the parent block page will handle the insets
    if (suppressSystemInsets) return {};

    const insetStyles: IInsetStyles = getStyleForInsets(insets);
    let insetStyle: ViewStyle = insetStyles.mid;

    // apply inset for first block
    if (index === 0) {
      insetStyle = {
        ...insetStyles.top,
        paddingTop:
          topBarStyle === NativeTopBarStyle.Hidden ||
          presentationStyle === PresentationStyle.KitModal
            ? 0
            : insetStyles.top.paddingTop +
              (align /*vertical*/ === 'center' ? 0 : blockPageDeltaInsetTop),
      };
    }

    // apply inset for last block
    if (index === blocksData.length - 1 && contentHeight >= viewportHeight) {
      insetStyle = {
        // ARTEMIS-1237: Preserve top inset for case where page has a single block.
        ...insetStyle,

        ...insetStyles.bottom,
        paddingBottom:
          presentationStyle === PresentationStyle.KitModal
            ? 0
            : insetStyles.bottom.paddingBottom +
              (align /*vertical*/ === 'center' ? 0 : blockPageDeltaInsetBottom),
      };
    }

    return insetStyle;
  };

  const getBlockExtensions = (
    index: number
  ): { extendTop?: number; extendBottom?: number } => {
    // Only provide extensions on iOS where scroll views bounce
    if (Platform.OS !== 'ios') return {};

    const extensions: { extendTop?: number; extendBottom?: number } = {};
    const fixedExtensionHeight = 400; // Fixed height for bounce coverage

    // First block gets top extension
    if (index === 0) {
      extensions.extendTop = fixedExtensionHeight;
    }

    // Last block gets bottom extension only if content fills the viewport
    // (meaning the last block is actually adjacent to the bottom)
    if (index === blocksData.length - 1 && contentHeight >= viewportHeight) {
      extensions.extendBottom = fixedExtensionHeight;
    }

    return extensions;
  };

  const contextData = {
    addressRequired,
    align,
    animationEnabled,
    appKey,
    authProviderId,
    blockPageDeltaInsetTop,
    blockPageDeltaInsetBottom,
    blocksData,
    clientBrandColor,
    contentData,
    guestToken,
    includeCloseButton,
    includeLogoutButton,
    isBlockPageEdited,
    isBlockPageLoading,
    isBlockPageRefreshing,
    isBlockPageReplacing,
    largeSaveButtonEnabled,
    blockPageUrl,
    renderBlock,
    dispatchAction,
    onDismiss,
    refreshBlockPage,
    setCurrentTopBarStyle,
    setIsBlockPageEdited,
    setIsBlockPageLoading,
    setIsBlockPageRefreshing,
    submitActionRef,
    getInsetStyleForBlock,
    getBlockExtensions,
    setContentHeight,
    setViewportHeight,
  };

  return (
    <BlockPageContext.Provider value={contextData}>
      {children}
    </BlockPageContext.Provider>
  );
};

export const useBlockPageContext = (): ContextData => {
  return useContext<ContextData>(BlockPageContext);
};
